import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { Copy } from 'lucide-react';

import { AppVersion, InviteLinkButton, ScrollArea } from '@app/shared/ui/atoms/Common';
import {
  AboutLabel,
  BottomBar,
  CuratorInfo,
  NameSection,
  PowerCard,
  TopBar,
} from '@app/shared/ui/molecules/Profile';
import {
  useGetCurrentUserQuery,
  useGetCurrentUserProfileQuery,
} from '@app/store/slices/users/usersApi';
import { useDynamicTranslationNamespace } from '@app/shared/hooks/useDynamicTranslationNamespace';
import { useGetCurrentUserProgressQuery } from '@app/store/slices/userProgress/userProgressApi';
import { useBalance } from '@app/shared/hooks/useBalance';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { telegramVibrate, generateReferralLink } from '@app/shared/utils/common';
import { getTags } from '@app/shared/utils/userProfile';
import { UserRole } from '@app/store/slices/users/usersTypes';
import { checkIcon } from '@app/assets';

export default function ProfilePage() {
  const [isRefLinkCopied, setIsRefLinkCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { data: user, isError: isUserError } = useGetCurrentUserQuery();
  const {
    data: userProfile,
    isLoading: isUserProfileLoading,
    isError: isUserProfileError,
  } = useGetCurrentUserProfileQuery();

  const { data: userProgress, isError: isUserProgressError } = useGetCurrentUserProgressQuery();
  const { tonTotalIncome, isError: isBalanceError } = useBalance();
  const { t } = useTranslation();
  const [, copyToClipboard] = useCopyToClipboard();
  useDynamicTranslationNamespace('countries');

  useDisplayError(
    isUserError || isUserProfileError || isUserProgressError || isBalanceError,
    t('errors.failed_to_load_user')
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleCopyToClipboard = () => {
    if (!userProfile || isRefLinkCopied) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    telegramVibrate();
    const referralLink = generateReferralLink(userProfile.userId);
    void copyToClipboard(referralLink).then(() => {
      setIsRefLinkCopied(true);
      timeoutRef.current = setTimeout(() => {
        setIsRefLinkCopied(false);
        timeoutRef.current = null;
      }, 2000);
    });
  };

  const userLevel = userProgress?.level ? userProgress.level + 1 : 1;
  const curator = userProfile?.curator;
  const tags = getTags(userProfile?.country, userProfile?.city, userProfile?.activitiesScope, t);

  return (
    <ScrollArea className="gap-4">
      <TopBar isMyProfile isSuperUser={user?.role === UserRole.SUPER} />
      <NameSection
        name={userProfile?.fullName}
        avatar={userProfile?.avatar}
        level={userLevel}
        peopleCount={userProfile?.refUsersCount}
        diamondsCount={tonTotalIncome}
        isUserLoading={isUserProfileLoading}
      />
      <PowerCard
        title={userProfile?.superPower?.title}
        description={userProfile?.superPower?.data}
        showPower={userProfile?.showSuperPower ?? true}
      />
      {curator && <CuratorInfo telegramName={curator.telegramUsername} name={curator.fullName} />}
      {userProfile?.bio && <AboutLabel text={userProfile.bio} />}
      <BottomBar
        tags={tags}
        instagram={userProfile?.instagramTag}
        telegram={userProfile?.telegramUsername}
      />
      <InviteLinkButton
        icon={
          isRefLinkCopied ? (
            <img src={checkIcon} alt="checkmark" className="size-[18px]" />
          ) : (
            <Copy size={18} />
          )
        }
        onClick={handleCopyToClipboard}
      />
      {<AppVersion className="text-foreground" />}
    </ScrollArea>
  );
}
